import { useState, useEffect } from 'react';
import {
  Box, Stack, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
 
import {
  Loader,
  Wrapper,
} from '../../components';
import { DashboardContent } from '../../components/dashboard';
import {
  Logs,
  dashboardCharts,
  dashboardSummary,
  dashboardCredits,
  dashboardRecentActiviy,
} from '../../services';
import { getAllApisWithSubscription } from '../../services/apiCatalogService';
import DateTimeGraph from '../../charts/line-charts/dashboard';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
 
export const groupSimilarData = (arr = []) => {
  const apiCategory = arr.map(
    (item) => item?.attributes?.apiCategory?.data?.attributes?.name,
  );
 
  const apiCategoryObj = {};
  apiCategory.forEach((element) => {
    apiCategoryObj[element] = [];
  });
 
  arr.forEach((item) => apiCategoryObj[item?.attributes?.apiCategory?.data?.attributes?.name].push({
    name: item?.attributes?.name,
    id: item?.id,
  }));
  return apiCategoryObj;
};
 
export function Dashboard() {
  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  const [dashboardSummaryData, setDashboardSummaryData] = useState(null);
  const [dashboardCreditData, setDashboardCreditData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const jwtToken = user?.jwt;
  const navigate = useNavigate();
  
   // derive credits from API data
   const totalCredits = dashboardCreditData?.totalAssignedCredits || 0;
   const usedCredits =
     (dashboardCreditData?.totalAssignedCredits || 0) -
     (dashboardCreditData?.totalRemainingCredits || 0);
   const remainingCredits = dashboardCreditData?.totalRemainingCredits || 0;
   const testingCredits = Math.floor(usedCredits * 0.4);
   const liveCredits = usedCredits - testingCredits;

  const [totalApis, setTotalApis] = useState(0);
  const [loading, setLoading] = useState(true);

  const formatRecentActivity = (item) => {
    let status = "";
    let statusColor = "#64748b";
    let title = "Activity";
  
    switch (item.type) {
      case "api_call":
        status = item.metadata?.statusCode === 200 ? "Success" : "Failed";
        statusColor =
          item.metadata?.statusCode === 200 ? "#10b981" : "#ef4444";
        title = "API Call";
        break;
  
      case "credit_purchase":
        status = "Completed";
        statusColor = "#10b981";
        title = "Credit Purchase";
        break;
  
      case "subscription":
        status = "Active";
        statusColor = "#6366f1";
        title = "Subscription";
        break;
  
      case "support_mail":
        status = "Sent";
        statusColor = "#3b82f6";
        title = "Support Mail";
        break;
  
      case "key_generation":
        status = "Generated";
        statusColor = "#8b5cf6";
        title = "Key Generation";
        break;
  
      default:
        break;
    }
  
    return {
      type: item.type,
      title,
      description: item.description,
      time: dayjs(item.timestamp).fromNow(),
      status,
      statusColor,
    };
  };  
 
  useEffect(() => {
    if (!jwtToken) return;
  
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
  
        const summaryRes = await dashboardSummary(jwtToken);
        if (summaryRes?.status === 200) {
          setDashboardSummaryData(summaryRes.data);
        }
  
        const dashboardCreditData = await dashboardCredits(jwtToken);
        if (dashboardCreditData?.status === 200) {
          setDashboardCreditData(dashboardCreditData.data);
        }
  
        const allApiWithSubscription = await getAllApisWithSubscription();
        if (allApiWithSubscription?.status === 200) {
          const responseData = allApiWithSubscription?.data?.data || {};
          const subscribedCount = responseData?.totalSubscribedApis || 0;
          setTotalApis(subscribedCount);
        }

        const dashboardActivity = await dashboardRecentActiviy(jwtToken);
        if (dashboardActivity?.status === 200) {
          const activities = (dashboardActivity?.data?.data || []).slice(0, 3).map(formatRecentActivity);
        setRecentActivities(activities);
        }
  
      } catch (error) {
        Logs(error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchDashboardData();
  }, [jwtToken]);
  
  return (
    <Wrapper>

    {loading && <Loader />}
      <Stack sx={{ pt: 2, mt: 9 }} spacing={4} className="wrapper">
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: "#65758B",
                fontSize: "14px",
              }}
            >
              <Typography variant="h4" fontWeight="bold" color="black">
              Welcome back, {user?.username}!
            </Typography>
            <Typography color="black" fontWeight="regular" sx={{ mt: 0.5, opacity: 0.8 }}>
              Here’s what’s happening with your APIs today.
            </Typography>
            </Typography>
          </Box>
        
        <DashboardContent
          loading={loading}
          totalCredits={totalCredits}
          usedCredits={usedCredits}
          remainingCredits={remainingCredits}
          testingCredits={testingCredits}
          liveCredits={liveCredits}
          monthlyRequests={dashboardSummaryData?.currentMonth?.totalRequests || 0}
          successRateChange={dashboardSummaryData?.successRateChangePercentage || 0}
          totalRequestsChangePercentage={dashboardSummaryData?.totalRequestsChangePercentage || 0}
          currentMonthSuccessRate={dashboardSummaryData?.currentMonth?.successRate || 0}
          totalApis={totalApis}
          recentActivities={recentActivities}
          onNavigate={(path) => navigate(path)}
          onViewAllActivity={() => navigate('/detailed-logs', { state: { showRecentActivities: true } })}
          usagePercentage={dashboardCreditData?.usagePercentage || 0}
        />
        
      </Stack>
    </Wrapper>
  );
}